/**
 * useListingColumnVisibilityState — column visibility state + per-header
 * column menu state.
 *
 * A10 from listing-abstraction-opportunities.md
 *
 * Manages visible column keys, derives the visible column array and column
 * visibility items list, and tracks per-header column menu open/anchor state.
 *
 * The caller injects column builders so this hook stays domain-neutral.
 */
import { useCallback, useMemo, useState } from 'react';
export function useListingColumnVisibilityState({ policy, initialVisibleColumnKeys, buildVisibleColumns, buildVisibilityItems, nonSortableKeys = [] }) {
    const [visibleColumnKeys, setVisibleColumnKeys] = useState(initialVisibleColumnKeys);
    const [colMenuOpen, setColMenuOpen] = useState(false);
    const [colMenuAnchor, setColMenuAnchor] = useState(null);
    const visibleColumns = useMemo(() => {
        const allowed = new Set(policy.allowed);
        return buildVisibleColumns(visibleColumnKeys.filter((k) => allowed.has(k)));
    }, [visibleColumnKeys, policy.allowed, buildVisibleColumns]);
    const columnVisibilityItems = useMemo(() => buildVisibilityItems(policy), [policy, buildVisibilityItems]);
    const sortableColumns = useMemo(() => visibleColumns.map((c) => c.dataKey).filter((dk) => !nonSortableKeys.includes(dk)), [visibleColumns, nonSortableKeys]);
    const handleColumnVisibilityApply = useCallback((keys) => {
        setVisibleColumnKeys(keys);
    }, []);
    const handleColumnMenuOpen = useCallback((_dataKey, anchor) => {
        setColMenuAnchor(anchor);
        setColMenuOpen(true);
    }, []);
    const handleColMenuOpenChange = useCallback((open) => {
        setColMenuOpen(open);
        if (!open)
            setColMenuAnchor(null);
    }, []);
    return {
        visibleColumnKeys,
        setVisibleColumnKeys,
        visibleColumns,
        columnVisibilityItems,
        sortableColumns,
        handleColumnVisibilityApply,
        colMenuOpen,
        colMenuAnchor,
        handleColumnMenuOpen,
        handleColMenuOpenChange
    };
}
